import { Field, Form, Formik, useFormikContext } from "formik";
import React from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import * as Yup from "yup";
import { ReactComponent as RightArrow } from "../../../assets/icons/right-arrow.svg";
import Button from "../../../components/Button";
import { Checkbox } from "../../../components/Checkbox";
import { Switch } from "../../../components/Switch";
import FormField from "../../../components/FormField";
import { PasswordField } from "../../../components/PasswordField";
import { ShowToast } from "../../../components/toast";
import { api } from "../../../helpers/api";
import { isValidUserRole } from "../../../helpers/permissions";
import { setToken, setUser } from "../../../helpers/utils";
import type { AuthResponse } from "../../../types/linked-accounts";
import { AuthSplitLayout } from "../components/AuthSplitLayout";
import { EmailOtpField, OtpErrorCode } from "./EmailOtpField";

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .matches(/^[^\s+@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address")
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password is too short")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/g,
      "Password must contain at least one uppercase and one lowercase letter and one special character"
    ),
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  marketing: Yup.boolean().optional(),
});

function RegisterForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Marketing signup is triggered by either the dedicated /register-marketing
  // path or the legacy /register?type=marketing query param. Both stay
  // supported indefinitely so existing campaign links don't break.
  const isMarketingFromUrl =
    location.pathname === "/register-marketing" ||
    searchParams.get("type") === "marketing";
  const [verificationToken, setVerificationToken] = React.useState<
    string | null
  >(null);
  // Bumped to remount the email step when the server rejects the token (e.g. it expired).
  const [emailStepKey, setEmailStepKey] = React.useState(0);

  return (
    <AuthSplitLayout
      leftVariant={isMarketingFromUrl ? "marketing" : "default"}
    >
      <Formik
        initialValues={{
          email: "",
          password: "",
          firstName: "",
          lastName: "",
          marketing: false,
        }}
        isInitialValid={false}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          if (!verificationToken) return;
          try {
            const res = await api.register({
              email: values.email,
              password: values.password,
              firstName: values.firstName,
              lastName: values.lastName,
              userType: isMarketingFromUrl || values.marketing ? "MarketingOnly" : "U",
              emailVerificationToken: verificationToken,
            });
            const result = res.data as AuthResponse;
            if (result?.accessToken) {
              ShowToast({
                message: "You've successfully created an autumn account.",
                type: "success",
              });
              setToken(result.accessToken);
              if (result.id) {
                const userResponse = await api.getUserProfile(result.id);
                setUser({ ...result, ...userResponse });
                const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
                if (!isValidUserRole(storedUser.type)) {
                  console.error(`Registration: invalid role stored: ${storedUser.type}`);
                }
                await api.setActiveStatus(result.id);
              }
              navigate("/onboarding");
            }
          } catch (err: unknown) {
            const e = err as {
              response?: { data?: { message?: string; code?: string } };
              message?: string;
            };
            if (e?.response?.data?.code === OtpErrorCode.EmailNotVerified) {
              setVerificationToken(null);
              setEmailStepKey((k) => k + 1);
            }
            ShowToast({
              message: e?.response?.data?.message || e?.message || "Registration failed. Please try again.",
              type: "error",
            });
          } finally {
            actions.setSubmitting(false);
          }
        }}
          >
        <RegisterFormContent
          showMarketingCheckbox={!isMarketingFromUrl}
          emailVerified={!!verificationToken}
          emailField={
            <EmailOtpField
              key={emailStepKey}
              onVerified={setVerificationToken}
            />
          }
        />
      </Formik>
    </AuthSplitLayout>
  );
}

function RegisterFormContent({
  showMarketingCheckbox,
  emailVerified,
  emailField,
}: {
  showMarketingCheckbox: boolean;
  emailVerified: boolean;
  emailField: React.ReactNode;
}) {
  const { isValid, isSubmitting, values } =
    useFormikContext<{ marketing: boolean }>();
  const [agreed, setAgreed] = React.useState(false);

  // AuthSplitLayout already wraps us in the cream-soft card with the
  // 12px radius + left-edge cutout. Form lives directly inside it
  // without an extra surface wrapper.
  return (
    <>
      <div className="flex flex-col items-center text-center gap-1.5">
        <h1
          className="text-[22px] font-semibold tracking-[-0.01em] text-[var(--d-ink)]"
          style={{ fontFamily: "var(--d-font-sans)" }}
        >
          Create your Autumn account
        </h1>
        <p
          className="text-[13px] leading-[1.5] text-[var(--d-ink-muted)]"
          style={{ fontFamily: "var(--d-font-body)" }}
        >
          Get started in a minute.
        </p>
      </div>

      <Form className="flex flex-col gap-5">
        <div className="flex flex-col gap-4">
          {emailField}
          {/* Mobile: stack vertically, each wrapper is `w-full` so
              inputs fill the row.
              Desktop (sm+): flex-row, both wrappers `flex-1` to share
              the row evenly. `[&_input]:w-full` makes the inner
              `<input>` (which renders bare without width) stretch to
              its wrapper. */}
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-3">
            <div className="w-full sm:flex-1 min-w-0 [&>div]:w-full [&_input]:w-full">
              <FormField
                label="First name"
                name="firstName"
                type="text"
                placeholder="Jane"
              />
            </div>
            <div className="w-full sm:flex-1 min-w-0 [&>div]:w-full [&_input]:w-full">
              <FormField
                label="Last name"
                name="lastName"
                type="text"
                placeholder="Doe"
              />
            </div>
          </div>
          <PasswordField
            name="password"
            label="Password"
            placeholder="Create a password"
          />
        </div>

        {/* Plan choice — a distinct, bordered product option (UX-13/14).
            This sets the account type, so it reads as a plan selection,
            not a legal opt-in. Hidden when the marketing plan is already
            implied by the invite link (showMarketingCheckbox === false);
            in that case the URL forces MarketingOnly at submit. */}
        {showMarketingCheckbox && (
          <Field name="marketing">
          {({
            field,
            form,
          }: {
            field: { value: boolean; name: string };
            form: { setFieldValue: (field: string, value: boolean) => void };
          }) => (
            <div
              className={`flex items-center justify-between gap-3 rounded-md border p-3.5 transition-colors ${
                field.value
                  ? "border-[var(--d-accent-deep)] bg-[var(--d-accent-tint)]"
                  : "border-[var(--d-rule)] bg-[var(--d-surface)]"
              }`}
            >
              <div className="flex flex-col gap-0.5">
                <span
                  className="text-[13px] font-semibold text-[var(--d-ink)]"
                  style={{ fontFamily: "var(--d-font-sans)" }}
                >
                  Marketing-only plan
                </span>
                <span
                  className="text-[12px] leading-[1.45] text-[var(--d-ink-muted)]"
                  style={{ fontFamily: "var(--d-font-body)" }}
                >
                  Marketing analytics only. No pricing, calendar, or dashboard
                  tools. Leave off for the full hotel-management account.
                </span>
                {values.marketing && (
                  <span
                    className="text-[12px] italic leading-[1.45] text-[var(--d-ink-muted)]"
                    style={{ fontFamily: "var(--d-font-body)" }}
                  >
                    If this was pre-selected from your invite link, leave it on.
                  </span>
                )}
              </div>
              <Switch
                checked={field.value}
                onChange={() => form.setFieldValue(field.name, !field.value)}
                className="shrink-0"
              />
            </div>
          )}
          </Field>
        )}

        {/* Legal consent — separate fine print directly above the submit
            button; a required agreement, not a product choice (UX-13). */}
        <label className="flex items-start gap-2.5 cursor-pointer">
          <Checkbox
            name="agree"
            id="agree"
            onChange={() => setAgreed((prev) => !prev)}
            checked={agreed}
          />
          <span
            className="text-[13px] leading-[1.5] text-[var(--d-ink-muted)]"
            style={{ fontFamily: "var(--d-font-body)" }}
          >
            I agree to the{" "}
            <a
              href="https://findautumn.com/terms-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[var(--d-accent-deep)] hover:text-[var(--d-accent)] transition-colors"
            >
              Terms &amp; Conditions
            </a>{" "}
            and{" "}
            <a
              href="https://findautumn.com/terms-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[var(--d-accent-deep)] hover:text-[var(--d-accent)] transition-colors"
            >
              Privacy Policy
            </a>
            .
          </span>
        </label>

        <Button
          className="w-full !justify-center"
          type="submit"
          disabled={!isValid || !agreed || !emailVerified}
          loading={isSubmitting}
        >
          <span className="inline-flex items-center gap-2">
            Sign up
            <RightArrow />
          </span>
        </Button>
      </Form>

      <p
        className="text-[13px] text-center text-[var(--d-ink-muted)]"
        style={{ fontFamily: "var(--d-font-body)" }}
      >
        Already have an account?{" "}
        <Link
          to="/login"
          className="font-medium text-[var(--d-accent-deep)] hover:text-[var(--d-accent)] hover:underline transition-colors"
        >
          Sign in
        </Link>
      </p>
    </>
  );
}

export default RegisterForm;
